// src/hooks/useUpgradeGate.js
//
// Imperative gate for components that can't easily wrap their UI in
// <LockedFeature>. Returns a `check` function: returns true when the user's
// plan unlocks the feature, otherwise opens the global upgrade modal and
// returns false.
//
// Usage:
//   const { check, isUnlocked } = useUpgradeGate();
//   const handleClick = () => {
//     if (!check('aiInsights', 'premium', { promoText: 'AI duplicate detection' })) return;
//     doTheThing();
//   };
//
import { useCallback } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';

export const useUpgradeGate = () => {
  const { hasFeature, subscription } = useSubscription();
  const { open } = useUpgradeModal();

  const isUnlocked = useCallback(
    (feature) => (feature ? hasFeature(feature) : false),
    [hasFeature]
  );

  const check = useCallback(
    (feature, requiredPlan = 'premium', modalProps = {}) => {
      if (isUnlocked(feature)) return true;
      open({
        feature,
        requiredPlan,
        currentPlan: subscription?.plan || 'free',
        promoText: modalProps.promoText || feature || '',
        message: modalProps.message,
        source: modalProps.source || 'user',
        ...modalProps,
      });
      return false;
    },
    [isUnlocked, open, subscription]
  );

  return { check, isUnlocked };
};

export default useUpgradeGate;
